import { db, loadTable, whenPersisted } from './core';

export type CoverEntityType = 'workspace' | 'project' | 'flow' | 'brand';

async function svgImage(file: File): Promise<HTMLImageElement> {
  const document = new DOMParser().parseFromString(await file.text(), 'image/svg+xml');
  const svg = document.documentElement;
  if (document.querySelector('parsererror') || svg.localName !== 'svg' || svg.namespaceURI !== 'http://www.w3.org/2000/svg') {
    throw new Error('Could not open this SVG. Choose a valid SVG image.');
  }
  const viewBox = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
  // Give dimensionless exports a viewport with the same proportions as their viewBox.
  if (viewBox.length === 4 && viewBox.every(Number.isFinite) && viewBox[2] > 0 && viewBox[3] > 0) {
    if (!svg.hasAttribute('width') || svg.getAttribute('width')?.endsWith('%')) svg.setAttribute('width', String(viewBox[2]));
    if (!svg.hasAttribute('height') || svg.getAttribute('height')?.endsWith('%')) svg.setAttribute('height', String(viewBox[3]));
  }
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' }));
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Could not open this SVG. Choose a valid SVG image.'));
      image.src = url;
    });
    return image;
  } finally { URL.revokeObjectURL(url); }
}

function imageDataUrl(file: File): Promise<string> {
  const isSvg = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
  if (!isSvg && !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    throw new Error('Choose a JPEG, PNG, WebP or SVG image.');
  }
  if (file.size > 20 * 1024 * 1024) throw new Error('Image must be 20 MB or smaller.');

  return new Promise((resolve, reject) => {
    (isSvg ? svgImage(file) : createImageBitmap(file)).then(bitmap => {
      const width = bitmap instanceof HTMLImageElement ? bitmap.naturalWidth : bitmap.width;
      const height = bitmap instanceof HTMLImageElement ? bitmap.naturalHeight : bitmap.height;
      const scale = Math.min(isSvg ? Infinity : 1, 1800 / width, 1400 / height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const context = canvas.getContext('2d');
      if (!context) { if ('close' in bitmap) bitmap.close(); reject(new Error('Your browser could not prepare this image.')); return; }
      if (!isSvg) {
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
      }
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      if ('close' in bitmap) bitmap.close();
      canvas.toBlob(blob => {
        if (!blob) { reject(new Error('Your browser could not prepare this image.')); return; }
        const reader = new FileReader();
        reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Could not read the image.'));
        reader.onerror = () => reject(new Error('Could not read the image.'));
        reader.readAsDataURL(blob);
      }, isSvg ? 'image/png' : 'image/jpeg', 0.86);
    }).catch(() => reject(new Error('Could not open this image. Try a JPEG, PNG, WebP or SVG file.')));
  });
}

async function upload(file: File, name: string, type: CoverEntityType, id: string): Promise<string> {
  const response = await fetch('/api/v1/uploads', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: `${name} cover`, dataUrl: await imageDataUrl(file), coverEntityType: type, coverEntityId: id }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body.assetId) throw new Error(body.error || 'Image upload failed. Please try again.');
  return body.assetId;
}

export async function setEntityCover(type: CoverEntityType, id: string, file: File): Promise<void> {
  const table = `${type}s` as 'workspaces' | 'projects' | 'flows' | 'brands';
  await loadTable(table);
  const entity = db[table].find(row => row.id === id);
  if (!entity) throw new Error('Could not find this item. Refresh the page and try again.');

  await whenPersisted(entity);
  const assetId = await upload(file, entity.name || type, type, entity.id!);
  const response = await fetch(`/api/v1/${table}/${entity.id}/cover`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ assetId }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || 'Image uploaded, but the cover could not be saved. Please try again.');

  Object.assign(entity, body);
  window.dispatchEvent(new CustomEvent('cf:data-updated', { detail: table }));
}
